import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { useGlobalSearch } from "../../hooks/useGlobalSearch";
import posthog from "posthog-js";
import { logSearchClick, type SearchResult } from "../../api/search";

const quickActions = [
  { id: "open-candidate", title: "Go to candidate dashboard", url: "/candidate/dashboard" },
  { id: "open-employer", title: "Go to employer console", url: "/employer/dashboard" },
  { id: "contact", title: "Contact Lighthouse", url: "/contact" },
];

const RECENT_STORAGE_KEY = "lighthouse_command_recents";
const MAX_RECENTS = 5;

type RecentCommand = {
  title: string;
  url: string;
  type?: string;
  subtitle?: string;
};

type HandleSelectMeta = {
  searchResult?: SearchResult;
  labelOverride?: {
    title: string;
    subtitle?: string;
    type?: string;
  };
};

const loadRecents = (): RecentCommand[] => {
  try {
    const raw = localStorage.getItem(RECENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const persistRecents = (items: RecentCommand[]) => {
  try {
    localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore
  }
};

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [recents, setRecents] = useState<RecentCommand[]>([]);
  const navigate = useNavigate();
  const { data: results = [], isFetching } = useGlobalSearch(query, open);

  const updateRecents = (entry: RecentCommand) => {
    setRecents((current) => {
      const deduped = [entry, ...current.filter((item) => item.url !== entry.url)].slice(0, MAX_RECENTS);
      persistRecents(deduped);
      return deduped;
    });
  };

  useEffect(() => {
    if (!open) return;
    setRecents(loadRecents());
  }, [open]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    const handleOpenEvent = () => setOpen(true);
    window.addEventListener("keydown", handleKey);
    window.addEventListener("open-command-palette", handleOpenEvent as EventListener);
    return () => {
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("open-command-palette", handleOpenEvent as EventListener);
    };
  }, []);

  const grouped = useMemo(() => {
    return {
      jobs: results.filter((result) => result.type === "job"),
      companies: results.filter((result) => result.type === "company"),
      pages: results.filter((result) => result.type === "page"),
    };
  }, [results]);

  const handleSelect = (url: string, meta?: HandleSelectMeta) => {
    const querySnapshot = query;
    setOpen(false);
    setQuery("");
    const selection = meta?.searchResult
      ? {
          title: meta.searchResult.title,
          subtitle: meta.searchResult.subtitle,
          type: meta.searchResult.type,
        }
      : meta?.labelOverride;
    posthog.capture("command_palette_select", {
      url,
      query: querySnapshot,
      source: meta?.searchResult ? "search_result" : "command_shortcut",
      result_type: selection?.type,
      title: selection?.title,
    });
    if (meta?.searchResult) {
      void logSearchClick({
        resultId: meta.searchResult.id,
        url,
        type: meta.searchResult.type,
        rank: meta.searchResult.rank,
        score: meta.searchResult.score,
        query: querySnapshot,
        title: meta.searchResult.title,
        subtitle: meta.searchResult.subtitle,
      });
    }
    if (selection?.title) {
      updateRecents({ title: selection.title, url, type: selection.type, subtitle: selection.subtitle });
    }
    navigate(url);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen} title="Search Lighthouse">
      <CommandInput
        autoFocus
        value={query}
        onValueChange={setQuery}
        placeholder="Search jobs, companies, pages"
      />
      <CommandList>
        <CommandEmpty>{isFetching ? "Looking for matches..." : "No matches found."}</CommandEmpty>
        {query.length === 0 && recents.length > 0 && (
          <CommandGroup heading="Recent">
            {recents.map((recent) => (
              <CommandItem
                key={recent.url}
                value={recent.title}
                onSelect={() =>
                  handleSelect(recent.url, {
                    labelOverride: { title: recent.title, subtitle: recent.subtitle, type: recent.type },
                  })
                }
              >
                {recent.title}
                {recent.subtitle && <span className="ml-auto text-xs text-muted-foreground">{recent.subtitle}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {grouped.jobs.length > 0 && (
          <CommandGroup heading="Jobs">
            {grouped.jobs.map((result) => (
              <CommandItem key={result.id} value={result.title} onSelect={() => handleSelect(result.url, { searchResult: result })}>
                {result.title}
                {result.subtitle && <span className="ml-auto text-xs text-muted-foreground">{result.subtitle}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {grouped.companies.length > 0 && (
          <CommandGroup heading="Companies">
            {grouped.companies.map((result) => (
              <CommandItem key={result.id} value={result.title} onSelect={() => handleSelect(result.url, { searchResult: result })}>
                {result.title}
                {result.subtitle && <span className="ml-auto text-xs text-muted-foreground">{result.subtitle}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {(grouped.pages.length > 0 || quickActions.length > 0) && (
          <CommandGroup heading="Pages & actions">
            {grouped.pages.map((result) => (
              <CommandItem key={result.id} value={result.title} onSelect={() => handleSelect(result.url, { searchResult: result })}>
                {result.title}
                {result.subtitle && <span className="ml-auto text-xs text-muted-foreground">{result.subtitle}</span>}
              </CommandItem>
            ))}
            {quickActions.map((action) => (
              <CommandItem
                key={action.id}
                value={action.title}
                onSelect={() =>
                  handleSelect(action.url, {
                    labelOverride: { title: action.title, type: "action" },
                  })
                }
              >
                {action.title}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}

